import { queryClient } from "@/lib/queryClient";
import { queryKeys } from "@/lib/queryKeys";
import { CACHE_TIME } from "@/lib/cacheConfig";
import {
  getMaterialCodeByFirstTierSupplier,
  getMaterialDetailByPartId,
  getMaterialSuppliers,
} from "@/api/materialSupplierService";
import { getBreakdownProcessV1, getChildPartV1 } from "@/api/breakdownService";
import {
  getMaterial2ndTierProcesses,
  getMaterialOtherProcesses,
  getChildPartProcesses,
  getOEMPartProcesses,
  getSparePartProcesses,
  getOEMPartOtherProcesses,
  getSparePartOtherProcesses,
  getDimensions,
  getSupplier2nd,
  getReturnScrap,
} from "@/api/masterService";
import { getPartSKPBySupplier, getFirstTierSuppliers } from "@/api/supplierService";
import { getAllSpecs } from "@/api/materialTypeService";
import { getLegacyMaterialCodes, getLegacyPartActive } from "@/api/migrationService";
import { getExchangeRateMaster } from "@/api/breakdownExchangeRateService";

export const fetchMaterialCodesBySupplier = (supplierCode: string) =>
  queryClient.ensureQueryData({
    queryKey: queryKeys.materialSupplier.materialCodesBySupplier(supplierCode),
    queryFn: () => getMaterialCodeByFirstTierSupplier(supplierCode),
    staleTime: CACHE_TIME.SHORT,
  });

export const fetchMaterialDetailByPartId = (partId: number, version: "v1" | "v2") =>
  queryClient.ensureQueryData({
    queryKey: queryKeys.materialSupplier.materialDetailByPartId(partId),
    queryFn: () => getMaterialDetailByPartId(partId, version),
    staleTime: CACHE_TIME.SHORT,
  });

export const fetchBreakdownProcessV1 = (supplierCode: string) =>
  queryClient.ensureQueryData({
    queryKey: queryKeys.breakdown.breakdownProcessV1(supplierCode),
    queryFn: () => getBreakdownProcessV1(supplierCode),
    staleTime: CACHE_TIME.SHORT,
  });

export const fetchChildPartV1ByPartId = (partId: number) =>
  queryClient.ensureQueryData({
    queryKey: queryKeys.breakdown.childPartV1(partId),
    queryFn: () => getChildPartV1(partId),
    staleTime: CACHE_TIME.SHORT,
  });

export const fetchPartSKPBySupplier = (supplierCode: string) =>
  queryClient.ensureQueryData({
    queryKey: queryKeys.firstTierSupplier.partSKPList(supplierCode),
    queryFn: () => getPartSKPBySupplier(supplierCode),
    staleTime: CACHE_TIME.MASTER_DATA,
  });

export const fetchMaterial2ndTierProcesses = () =>
  queryClient.ensureQueryData({
    queryKey: [queryKeys.masterProcess.getMaterial2ndTierProcesses()],
    queryFn: () => getMaterial2ndTierProcesses({}),
    staleTime: CACHE_TIME.MASTER_DATA,
  });

export const fetchMaterialOtherProcesses = () =>
  queryClient.ensureQueryData({
    queryKey: [queryKeys.masterProcess.getMaterialOtherProcesses()],
    queryFn: () => getMaterialOtherProcesses({}),
    staleTime: CACHE_TIME.MASTER_DATA,
  });

export const fetchChildPartProcesses = () =>
  queryClient.ensureQueryData({
    queryKey: [queryKeys.masterProcess.getChildPartProcesses()],
    queryFn: () => getChildPartProcesses({}),
    staleTime: CACHE_TIME.MASTER_DATA,
  });

export const fetchOEMPartProcesses = () =>
  queryClient.ensureQueryData({
    queryKey: [queryKeys.masterProcess.getOEMPartProcesses()],
    queryFn: () => getOEMPartProcesses({}),
    staleTime: CACHE_TIME.MASTER_DATA,
  });

export const fetchSparePartProcesses = () =>
  queryClient.ensureQueryData({
    queryKey: [queryKeys.masterProcess.getSparePartProcesses()],
    queryFn: () => getSparePartProcesses({}),
    staleTime: CACHE_TIME.MASTER_DATA,
  });

export const fetchOEMPartOtherProcesses = () =>
  queryClient.ensureQueryData({
    queryKey: [queryKeys.masterProcess.getOEMPartOtherProcesses()],
    queryFn: () => getOEMPartOtherProcesses({}),
    staleTime: CACHE_TIME.MASTER_DATA,
  });

export const fetchSparePartOtherProcesses = () =>
  queryClient.ensureQueryData({
    queryKey: [queryKeys.masterProcess.getSparePartOtherProcesses()],
    queryFn: () => getSparePartOtherProcesses({}),
    staleTime: CACHE_TIME.MASTER_DATA,
  });

export const fetchAllSpecs = () =>
  queryClient.ensureQueryData({
    queryKey: [queryKeys.materialTypes.listAllSpec()],
    queryFn: () => getAllSpecs(),
    staleTime: CACHE_TIME.MASTER_DATA,
  });

export const fetchDimensions = () =>
  queryClient.ensureQueryData({
    queryKey: queryKeys.master.dimensions(),
    queryFn: () => getDimensions(),
    staleTime: CACHE_TIME.MASTER_DATA,
  });

export const fetchMaterialSupplierList = () =>
  queryClient.ensureQueryData({
    queryKey: queryKeys.materialSupplier.listAll(),
    queryFn: () => getMaterialSuppliers({ skip: 0, limit: 1000, active_only: false }),
    staleTime: CACHE_TIME.MASTER_DATA,
  });

export const fetchSupplier2nd = () =>
  queryClient.ensureQueryData({
    queryKey: queryKeys.master.supplier2nd(),
    queryFn: () => getSupplier2nd({}),
    staleTime: CACHE_TIME.MASTER_DATA,
  });

export const fetchReturnScrap = () =>
  queryClient.ensureQueryData({
    queryKey: queryKeys.master.returnScrap(),
    queryFn: () => getReturnScrap({}),
    staleTime: CACHE_TIME.MASTER_DATA,
  });

export const fetchExchangeRateMaster = () =>
  queryClient.ensureQueryData({
    queryKey: queryKeys.breakdownExchangeRate.master(),
    queryFn: () => getExchangeRateMaster(),
    staleTime: CACHE_TIME.MASTER_DATA,
  });

export const fetchFirstTierSuppliers = () =>
  queryClient.ensureQueryData({
    queryKey: queryKeys.firstTierSupplier.list(),
    queryFn: () => getFirstTierSuppliers({ skip: 0, limit: 2000 }),
    staleTime: CACHE_TIME.MASTER_DATA,
  });

export const fetchLegacyMaterialCodes = (
  partNo: string,
  ac: string,
  rt: string,
  supplierCode: string,
) =>
  queryClient.ensureQueryData({
    queryKey: queryKeys.migration.legacyMaterialCodes(partNo, ac, rt, supplierCode),
    queryFn: () => getLegacyMaterialCodes(partNo, ac, rt, supplierCode),
    staleTime: CACHE_TIME.SHORT,
  });

export const fetchLegacyPartActive = (supplierCode: string) =>
  queryClient.ensureQueryData({
    queryKey: queryKeys.migration.legacyPartActive(supplierCode),
    queryFn: () => getLegacyPartActive(supplierCode),
    staleTime: CACHE_TIME.SHORT,
  });